import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm, useWatch } from "react-hook-form";
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
} from "@mui/material";
import moment from "moment";
import { debounce } from "lodash";

import AppForm from "components/Shared/AppForm";
import SearchCustomer from "components/Shared/SearchCustomer";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import RichTextEditor from "components/Shared/RichTextEditor";
import FileDropzone from "components/Shared/FileDropzone";

import { useLazyGetDDLSystemUserListQuery } from "store/services/userApi";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";

import { useAuth, useDOUploadFile } from "hooks";
import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useLazyGetDdlVendorProfileQuery } from "store/services/vendorProfileApi";
import { useLazySearchActiveShipmentsQuery } from "store/services/shipmentApi";
import BaseSelect from "components/Shared/Base/BaseSelect";
import { formHookInputHelper } from "utils/formHookField";

import { ENTITY_LIST_CONSTANTS } from "data/static";
import { useListListQuery } from "store/services/listService";

const SOURCE_OPTIONS = ["Vendor Profile", "Customer Profile"];
const SOURCE_VALUE_MAP = {
  "Vendor Profile": "VendorProfile",
  "Customer Profile": "Customer",
};
const ADMIN_ONLY_TICKET_TYPES = ["Damaged", "Missing"];

const INITIAL_VALUE = {
  entity: "",
  source: "",
  sourceRef: "",
  shipment: null,
  subject: "",
  stage: "Open",
  description: "",
  responsible: "",
  dueDate: "",
  attachments: [],
  isActive: true,
  ticketType: null,
  bankDetails: {
    bankName: "",
    bankAccountName: "",
    bankAccountNumber: "",
  },
  address: "",
};

const TicketForm = ({ ticket = null, isBusy = false, onSubmit = () => { } }) => {
  const navigate = useNavigate();
  const isInitialMount = useRef(true);

  const ticketStageEnums = ["Open", "In Progress", "Closed"];

  const { viewEntity, isAccountAdmin, isSystemAdmin, isVendor, isVendorStaff } =
    useAuth();

  const { handleUploadFiles, uploadingFiles } = useDOUploadFile();

  // react form hook
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
    setValue,
    getValues,
  } = useForm({
    defaultValues: INITIAL_VALUE,
  });

  const selectedEntity = watch("entity");
  const _watchAccount = useWatch({ control, name: "account" });
  const _watchSource = useWatch({ control, name: "source" });
  const _watchSourceRef = useWatch({ control, name: "sourceRef" });
  const _watchTicketType = useWatch({ control, name: "ticketType" });
  const currentStage = watch("stage");

  const isCODRequest = _watchTicketType?.label === "COD Request";
  const isPickupRequest = _watchTicketType?.label === "Pickup Request";
  const isShipmentIssue = ADMIN_ONLY_TICKET_TYPES.includes(
    _watchTicketType?.label
  );

  const {
    data: ticketTypes = [],
    isLoading: isLoadingTicketTypes,
    isFetching: isFetchingTicketTypes,
  } = useListListQuery(
    {
      type: ENTITY_LIST_CONSTANTS.TICKET_TYPE,
      status: "Active",
    },
    { skip: false, refetchOnMountOrArgChange: true }
  );

  const { data: accounts, isLoading: loadingAccounts } =
    useGetDDLAccountListQuery();

  const [fetchEntities, { data: entities, isLoading: loadingEntities }] =
    useLazyGetDDLEntityListQuery();

  const [
    fetchUsers,
    { data: userList, isLoading: loadingUsers, isFetching: fetchingUsers },
  ] = useLazyGetDDLSystemUserListQuery();

  const [
    fetchVendorProfile,
    { data: vendors, isLoading: loadingVendor, fetching: fetchingVendor },
  ] = useLazyGetDdlVendorProfileQuery();

  const [
    searchActiveShipments,
    { isFetching: fetchingShipments },
  ] = useLazySearchActiveShipmentsQuery();

  const [shipmentResults, setShipmentResults] = useState([]);

  const loading = isBusy || uploadingFiles || loadingUsers || fetchingUsers;

  const debouncedSearchShipments = useCallback(
    debounce(async (query) => {
      if (query && query.length >= 2) {
        try {
          const result = await searchActiveShipments({
            account: _watchAccount?._id || viewEntity?.entity?.account?._id,
            entity: selectedEntity?.id || viewEntity?.entity?._id,
            query,
          }).unwrap();
          setShipmentResults(result?.data || []);
        } catch {
          setShipmentResults([]);
        }
      } else {
        setShipmentResults([]);
      }
    }, 1000),
    [
      searchActiveShipments,
      _watchAccount?._id,
      selectedEntity?.id,
      viewEntity?.entity?._id,
      viewEntity?.entity?.account?._id,
    ]
  );

  // Check if form should be disabled
  const isFormDisabled = useMemo(() => {
    // Disable if ticket is closed in edit mode
    if (ticket && currentStage === "Closed") {
      return true;
    }
    // Keep existing vendor profile logic
    return ticket && ticket.source === "VendorProfile";
  }, [ticket, currentStage]);

  const _mappedEntities = useMemo(
    () =>
      entities?.map((entity) => ({
        label: entity?.name,
        id: entity?._id,
      })) ?? [],
    [entities]
  );

  const _mappedTicketTypes = useMemo(() => {
    const mapped = Array.isArray(ticketTypes)
      ? ticketTypes.map((item) => ({
        label: item?.name || "",
        _id: item?._id || "",
      }))
      : [];

    if (!isVendor && !isVendorStaff) {
      mapped.push(
        ...ADMIN_ONLY_TICKET_TYPES.map((name) => ({ label: name, _id: name }))
      );
    }

    const seen = new Set();
    return mapped.filter((opt) => {
      if (seen.has(opt.label)) return false;
      seen.add(opt.label);
      return true;
    });
  }, [ticketTypes, isVendor, isVendorStaff]);

  const _mappedUsers = useMemo(
    () =>
      userList?.map((el) => ({
        label: el?.name,
        id: el?._id,
      })) || [],
    [userList]
  );

  const sourceInfo = useMemo(() => {
    switch (_watchSource) {
      case "Vendor Profile":
        return {
          data: vendors || [],
          isBusy: loadingVendor || fetchingVendor,
        };
      default:
        return {
          data: [],
          isBusy: false,
        };
    }
  }, [_watchSource, vendors, loadingVendor, fetchingVendor]);

  // methods
  const handleSubmitTicket = async (data) => {
    // Don't submit if form is disabled
    if (isFormDisabled) return;

    delete data?.history;
    delete data.responses;

    const _ticketPayload = {
      ...data,
      responsible: data?.responsible?.id,
      source:
        SOURCE_VALUE_MAP[data?.source] ?? data?.source?.replace(/\s+/g, ""),
      sourceRef: data?.sourceRef?._id || data?.sourceRef,
      shipment: isShipmentIssue ? data?.shipment?._id || null : null,
      account: data?.entity?._id || viewEntity?.entity?.account?._id,
      entity: data?.entity?.id || viewEntity?.entity?._id,
      ticketType: data?.ticketType?._id,
      bankDetails: isCODRequest ? data.bankDetails : undefined,
      address: isPickupRequest ? data.address : undefined,
    };

    const [existingAttachments, newAttachments] = data?.attachments?.reduce(
      ([o, n], el) =>
        el?.publicId || el?.url ? [[...o, el], n] : [o, [...n, el]],
      [[], []]
    );

    if (newAttachments?.length) {
      const { uploadedFiles } = await handleUploadFiles(newAttachments, {
        folderName: "TICKET",
        entity: viewEntity?.entity?._id,
        account: viewEntity?.entity?.account?._id,
      });
      _ticketPayload.attachments = [...existingAttachments, ...uploadedFiles];
    }

    onSubmit(_ticketPayload);
  };

  const vendorBankAccounts = useMemo(() => {
    if (!_watchSourceRef) return [];
    const accounts = [];
    const seen = new Set();

    // Add active / existing from bankAccount array
    if (Array.isArray(_watchSourceRef?.bankAccount)) {
      _watchSourceRef.bankAccount.forEach((b) => {
        const accNo = b.bankAccountNumber != null ? String(b.bankAccountNumber) : "";
        if (b.bankName && !seen.has(accNo)) {
          seen.add(accNo);
          accounts.push({
            bankName: b.bankName,
            bankAccountName: b.bankAccountName || "",
            bankAccountNumber: accNo,
            label: `${b.bankName}${accNo ? ` - ${accNo}` : ""}${b.bankAccountName ? ` (${b.bankAccountName})` : ""}`,
          });
        }
      });
    }

    // Add vendorProfileBankDetail if not already in list
    if (_watchSourceRef?.vendorProfileBankDetail?.bankName) {
      const vDetail = _watchSourceRef.vendorProfileBankDetail;
      const accNo = vDetail.bankAccountNumber != null ? String(vDetail.bankAccountNumber) : "";
      if (!seen.has(accNo)) {
        seen.add(accNo);
        accounts.push({
          bankName: vDetail.bankName,
          bankAccountName: vDetail.bankAccountName || "",
          bankAccountNumber: accNo,
          label: `${vDetail.bankName}${accNo ? ` - ${accNo}` : ""}${vDetail.bankAccountName ? ` (${vDetail.bankAccountName})` : ""}`,
        });
      }
    }

    return accounts;
  }, [_watchSourceRef]);

  // Clear shipment when ticket type is not Damaged/Missing
  useEffect(() => {
    if (!isShipmentIssue) setValue("shipment", null);
  }, [isShipmentIssue, setValue]);

  // Auto-set subject for COD Request / Pickup Request on create
  useEffect(() => {
    if (!ticket) {
      if (isCODRequest) {
        setValue("subject", "COD Request");
      } else if (isPickupRequest) {
        setValue("subject", "Pickup Request");
      } else if (_watchTicketType && ["COD Request", "Pickup Request"].includes(getValues("subject"))) {
        setValue("subject", "");
      }
    }
  }, [_watchTicketType, setValue, ticket, getValues, isCODRequest, isPickupRequest]);

  // Auto-populate bank details when COD Request is selected (create mode only)
  useEffect(() => {
    if (!ticket && isCODRequest && vendorBankAccounts.length > 0) {
      const defaultBank = vendorBankAccounts[0];
      setValue("bankDetails.bankName", defaultBank.bankName || "");
      setValue("bankDetails.bankAccountName", defaultBank.bankAccountName || "");
      setValue("bankDetails.bankAccountNumber", defaultBank.bankAccountNumber || "");
    }
  }, [isCODRequest, vendorBankAccounts, setValue, ticket]);

  // Auto-populate address when Pickup Request is selected (create mode only)
  useEffect(() => {
    if (!ticket && isPickupRequest && _watchSourceRef?.address) {
      setValue("address", _watchSourceRef.address);
    }
  }, [isPickupRequest, _watchSourceRef, setValue, ticket]);

  // Initialize form with default values
  useEffect(() => {
    if (isInitialMount.current) {
      const defaultValues = {
        ...INITIAL_VALUE,
        entity: viewEntity?.entity
          ? {
            label: viewEntity.entity.name,
            id: viewEntity.entity._id,
          }
          : "",
        account: viewEntity?.entity?.account || "",
      };
      reset(defaultValues);
      isInitialMount.current = false;
    }
  }, [reset, viewEntity]);

  // Reset form when ticket changes
  useEffect(() => {
    if (!ticket) return;

    const ticketType = ticketTypes.find(
      (type) => type._id === (ticket.ticketType?._id || ticket.ticketType)
    );

    const resetData = {
      ...ticket,
      source:
        ticket?.source === "Customer"
          ? "Customer Profile"
          : ticket?.source
            ? ticket.source.replace(/([a-z])([A-Z])/g, "$1 $2")
            : "",
      entity: ticket?.entity
        ? {
          label: ticket.entity.name,
          id: ticket.entity._id,
        }
        : "",
      responsible: ticket?.responsible
        ? {
          label: ticket.responsible.designation
            ? `${ticket.responsible.name} | ${ticket.responsible.designation}`
            : ticket.responsible.name,
          id: ticket.responsible._id,
        }
        : "",
      ticketType: ticketType
        ? {
          _id: ticketType._id,
          label: ticketType.name,
        }
        : ADMIN_ONLY_TICKET_TYPES.includes(ticket?.ticketType?.name)
          ? {
            _id: ticket.ticketType.name,
            label: ticket.ticketType.name,
          }
          : null,
      shipment: ticket?.shipment
        ? {
          _id: ticket.shipment._id,
          shipmentId: ticket.shipment.shipmentId,
        }
        : null,
      dueDate: ticket?.dueDate
        ? moment(ticket.dueDate).format("YYYY-MM-DD")
        : "",
      bankDetails: ticket?.bankDetails || {
        bankName: "",
        bankAccountName: "",
        bankAccountNumber: "",
      },
      address: ticket?.address || "",
    };

    reset(resetData);
  }, [ticket, ticketTypes, reset]);

  // Fetch users when entity changes
  useEffect(() => {
    const entityId = selectedEntity?.id || viewEntity?.entity?._id;
    if (entityId) {
      fetchUsers({ entity: entityId });
    }
  }, [selectedEntity?.id, viewEntity?.entity?._id, fetchUsers]);

  // Fetch entities when account changes (for admins)
  useEffect(() => {
    if (isAccountAdmin || isSystemAdmin) {
      const accountId = _watchAccount?._id || viewEntity?.entity?.account?._id;
      if (accountId) {
        fetchEntities({ account: accountId });
      }
    }
  }, [
    _watchAccount?._id,
    viewEntity?.entity?.account?._id,
    fetchEntities,
    isAccountAdmin,
    isSystemAdmin,
  ]);

  // Fetch vendor profile when account and entity are selected
  useEffect(() => {
    if (_watchAccount?._id && selectedEntity?.id) {
      fetchVendorProfile({
        entity: selectedEntity.id,
        account: _watchAccount._id,
      });
    }
  }, [_watchAccount?._id, selectedEntity?.id, fetchVendorProfile]);

  // Set default values for vendors - run only once
  useEffect(() => {
    if (
      (isVendor || isVendorStaff) &&
      viewEntity?.entity?._id &&
      viewEntity?.entity?.account?._id
    ) {
      setValue("source", "Vendor Profile");
      fetchVendorProfile({
        entity: viewEntity.entity._id,
        account: viewEntity.entity.account._id,
      });
    }
  }, [
    isVendor,
    isVendorStaff,
    viewEntity?.entity?._id,
    viewEntity?.entity?.account?._id,
    setValue,
    fetchVendorProfile,
  ]);

  // Auto-select first vendor for vendors
  useEffect(() => {
    if (
      (isVendor || isVendorStaff) &&
      vendors &&
      vendors.length > 0 &&
      !watch("sourceRef")
    ) {
      setValue("sourceRef", vendors[0]);
    }
  }, [isVendor, isVendorStaff, vendors, setValue, watch]);

  return (
    <AppForm loading={loading}>
      <AppForm.Content>
        <Grid container spacing={6}>
          {!isVendor && (
            <>
              {(isAccountAdmin || isSystemAdmin) && (
                <Grid item md={6}>
                  <Controller
                    name="account"
                    control={control}
                    rules={{ required: "Please select an account." }}
                    render={({ field: { value, onChange } }) => (
                      <BaseAutocomplete
                        fullWidth
                        label="Account"
                        value={value || null}
                        onChange={(_, data) => {
                          onChange(data);
                          setValue("entity", "");
                          setValue("source", "");
                        }}
                        getOptionLabel={(option) => option?.name || ""}
                        isOptionEqualToValue={(option, value) =>
                          option?._id === value?._id
                        }
                        options={accounts || []}
                        loading={loadingAccounts}
                        loadingText="Loading Accounts..."

                      />
                    )}
                  />
                  {errors.account && (
                    <FormHelperText error>
                      {errors.account.message}
                    </FormHelperText>
                  )}
                </Grid>
              )}

              {(isAccountAdmin || isSystemAdmin) && (
                <Grid item md={6}>
                  <Controller
                    name="entity"
                    control={control}
                    rules={{ required: "Please select an entity." }}
                    render={({ field: { value, onChange } }) => (
                      <BaseAutocomplete
                        fullWidth
                        label="Entity"
                        value={value || null}
                        onChange={(_, data) => {
                          onChange(data);
                          setValue("responsible", "");
                          setValue("source", "");
                        }}
                        getOptionLabel={(option) => option?.label || ""}
                        isOptionEqualToValue={(option, value) =>
                          option?.id === value?.id
                        }
                        options={_mappedEntities}
                        loading={loadingEntities}
                        loadingText="Loading Entities..."
                      />
                    )}
                  />
                  {errors.entity && (
                    <FormHelperText error>
                      {errors.entity.message}
                    </FormHelperText>
                  )}
                </Grid>
              )}
            </>
          )}

          <Grid item md={6}>
            <Controller
              name="ticketType"
              control={control}
              rules={{ required: "Ticket Type is required." }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  required
                  label="Ticket Type"
                  value={value || null}
                  onChange={(_, data) => onChange(data)}
                  getOptionLabel={(option) => option?.label || ""}
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  options={_mappedTicketTypes}
                  loading={isLoadingTicketTypes || isFetchingTicketTypes}
                  loadingText="Loading Ticket Types..."
                  disabled={
                    isLoadingTicketTypes || isFetchingTicketTypes
                  }
                  helperText={errors?.ticketType?.message}
                  error={!!errors?.ticketType}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Ticket Type"
                      required
                      error={!!errors?.ticketType}
                      helperText={errors?.ticketType?.message}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {(isLoadingTicketTypes ||
                              isFetchingTicketTypes) && (
                                <CircularProgress color="inherit" size={20} />
                              )}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              )}
            />
          </Grid>

          {isShipmentIssue && (
            <Grid item md={6}>
              <Controller
                name="shipment"
                control={control}
                rules={{ required: "Please select a shipment." }}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    required
                    label="Shipment (Shipment ID)"
                    value={value || null}
                    onChange={(_, data) => onChange(data)}
                    onInputChange={(_, inputValue) =>
                      debouncedSearchShipments(inputValue)
                    }
                    getOptionLabel={(option) => option?.shipmentId || ""}
                    isOptionEqualToValue={(option, val) =>
                      option?._id === val?._id
                    }
                    options={shipmentResults}
                    loading={fetchingShipments}
                    loadingText="Searching Shipments..."
                    noOptionsText="Enter at least 2 characters to search active shipments by Shipment ID"
                    disabled={isFormDisabled}
                    error={!!errors?.shipment}
                    helperText={errors?.shipment?.message}
                  />
                )}
              />
            </Grid>
          )}

          {!isVendor && !isVendorStaff && (
            <Grid item xs={6}>
              <Controller
                name="source"
                control={control}
                render={(props) => (
                  <BaseSelect
                    {...formHookInputHelper(props)}
                    fullWidth
                    label="Source"
                    getOptionLabel={(opt) => opt}
                    isOptionEqualToValue={(opt, value) => opt === value}
                    onChange={(e) => {
                      props.field.onChange(e);
                      setValue("sourceRef", "");
                    }}
                    options={SOURCE_OPTIONS}
                  />
                )}
              />
            </Grid>
          )}

          {_watchSource === "Customer Profile" ? (
            <Grid item md={6}>
              <SearchCustomer
                control={control}
                name="sourceRef"
                label="Customer Profile"
                accountId={
                  _watchAccount?._id || viewEntity?.entity?.account?._id
                }
                entityId={selectedEntity?.id || viewEntity?.entity?._id}
                showAddButton={false}
                disabled={isFormDisabled}
              />
            </Grid>
          ) : (
            <Grid item md={6}>
              <Controller
                name="sourceRef"
                control={control}
                render={(props) => (
                  <BaseAutocomplete
                    {...formHookInputHelper(props)}
                    fullWidth
                    label={
                      isVendor || isVendorStaff
                        ? "Vendor Profile"
                        : _watchSource || "Select Source First"
                    }
                    onChange={(_, data) => {
                      props?.field?.onChange(data);
                    }}
                    getOptionLabel={(option) =>
                      option ? `${option?.ownerName} - ${option?.shopName}` : ""
                    }
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={sourceInfo.data}
                    loading={sourceInfo.isBusy}
                    loadingText="Loading Vendor Profiles..."
                  />
                )}
              />
            </Grid>
          )}

          <Grid item md={12}>
            <Controller
              name="subject"
              control={control}
              rules={{ required: "Please provide a subject." }}
              render={({ field }) => (
                <TextField
                  fullWidth
                  label="Subject"
                  type="text"
                  size="small"
                  required
                  {...field}
                />
              )}
            />
            {errors.subject && (
              <FormHelperText error>{errors.subject.message}</FormHelperText>
            )}
          </Grid>

          {/* COD Request - Bank Details */}
          {isCODRequest && (
            <>
              <Grid item xs={12}>
                <Alert severity="warning" variant="outlined">
                  <AlertTitle><strong>Important Notice: COD Payout Account</strong></AlertTitle>
                  Your COD funds and money transfers will be sent directly to this bank account. Please verify that the <strong>Bank Name</strong>, <strong>Account Name</strong>, and <strong>Account Number</strong> are completely accurate before submitting.
                </Alert>
              </Grid>

              <Grid item md={4}>
                <Controller
                  name="bankDetails.bankName"
                  control={control}
                  rules={{ required: "Bank Name is required." }}
                  render={({ field }) => (
                    <Autocomplete
                      freeSolo
                      fullWidth
                      size="small"
                      disabled={isFormDisabled}
                      options={vendorBankAccounts}
                      getOptionLabel={(option) => {
                        if (typeof option === "string") return option;
                        return option?.bankName || "";
                      }}
                      renderOption={(props, option) => (
                        <li {...props} key={option.bankAccountNumber || option.bankName}>
                          {option.label}
                        </li>
                      )}
                      value={field.value || ""}
                      onInputChange={(_, newInputValue) => {
                        field.onChange(newInputValue);
                      }}
                      onChange={(_, newValue) => {
                        if (newValue && typeof newValue === "object") {
                          setValue("bankDetails.bankName", newValue.bankName || "");
                          setValue("bankDetails.bankAccountName", newValue.bankAccountName || "");
                          setValue("bankDetails.bankAccountNumber", newValue.bankAccountNumber || "");
                        } else if (typeof newValue === "string") {
                          setValue("bankDetails.bankName", newValue);
                        } else {
                          setValue("bankDetails.bankName", "");
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Bank Name"
                          size="small"
                          required
                          error={!!errors?.bankDetails?.bankName}
                          helperText={errors?.bankDetails?.bankName?.message}
                          placeholder={
                            vendorBankAccounts.length > 0
                              ? "Select or enter bank name"
                              : "Enter bank name"
                          }
                        />
                      )}
                    />
                  )}
                />
                {errors?.bankDetails?.bankName && (
                  <FormHelperText error>
                    {errors.bankDetails.bankName.message}
                  </FormHelperText>
                )}
              </Grid>

              <Grid item md={4}>
                <Controller
                  name="bankDetails.bankAccountName"
                  control={control}
                  rules={{ required: "Account Name is required." }}
                  render={({ field }) => (
                    <TextField
                      fullWidth
                      label="Account Name"
                      size="small"
                      required
                      {...field}
                      disabled={isFormDisabled}
                    />
                  )}
                />
                {errors?.bankDetails?.bankAccountName && (
                  <FormHelperText error>
                    {errors.bankDetails.bankAccountName.message}
                  </FormHelperText>
                )}
              </Grid>

              <Grid item md={4}>
                <Controller
                  name="bankDetails.bankAccountNumber"
                  control={control}
                  rules={{ required: "Account Number is required." }}
                  render={({ field }) => (
                    <TextField
                      fullWidth
                      label="Account Number"
                      size="small"
                      required
                      {...field}
                      disabled={isFormDisabled}
                    />
                  )}
                />
                {errors?.bankDetails?.bankAccountNumber && (
                  <FormHelperText error>
                    {errors.bankDetails.bankAccountNumber.message}
                  </FormHelperText>
                )}
              </Grid>
            </>
          )}

          {/* Pickup Request - Address */}
          {isPickupRequest && (
            <Grid item md={12}>
              <Controller
                name="address"
                control={control}
                rules={{ required: "Address is required." }}
                render={({ field }) => (
                  <TextField
                    fullWidth
                    label="Address"
                    size="small"
                    required
                    multiline
                    rows={2}
                    {...field}
                    disabled={isFormDisabled}
                  />
                )}
              />
              {errors.address && (
                <FormHelperText error>{errors.address.message}</FormHelperText>
              )}
            </Grid>
          )}

          {!isVendor && (
            <>
              <Grid item md={6}>
                <Controller
                  name="stage"
                  control={control}
                  rules={{ required: "Please select a stage." }}
                  render={({ field }) => (
                    <FormControl fullWidth size="small">
                      <InputLabel id="ticket-stage">Stage</InputLabel>
                      <Select
                        fullWidth
                        labelId="ticket-stage"
                        label="Stage"
                        placeholder="Select stage"
                        value={field.value || ""}
                        readOnly={!ticket || isFormDisabled}
                        disabled={isFormDisabled}
                        {...field}
                      >
                        {ticketStageEnums.map((stage) => (
                          <MenuItem key={stage} value={stage}>
                            {stage}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
                {errors.stage && (
                  <FormHelperText error>{errors.stage.message}</FormHelperText>
                )}
              </Grid>

              <Grid item md={6}>
                <Controller
                  name="dueDate"
                  control={control}
                  rules={{ required: "Please provide a due date." }}
                  render={({ field }) => (
                    <TextField
                      required
                      fullWidth
                      label="Due Date"
                      type="date"
                      size="small"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      {...field}
                      disabled={isFormDisabled}
                    />
                  )}
                />
                {errors.dueDate && (
                  <FormHelperText error>
                    {errors.dueDate.message}
                  </FormHelperText>
                )}
              </Grid>

              <Grid item md={6}>
                <Controller
                  name="responsible"
                  control={control}
                  rules={{ required: "Please select a Responsible User." }}
                  render={({ field }) => (
                    <BaseAutocomplete
                      fullWidth
                      required
                      label="Responsible"
                      loading={loadingUsers || fetchingUsers}
                      loadingText="Loading Responsible Users..."
                      options={_mappedUsers}
                      value={field.value || null}
                      onChange={(_, data) => {
                        field.onChange(data);
                      }}
                      getOptionLabel={(option) => option?.label || ""}
                      isOptionEqualToValue={(option, value) =>
                        option?.id === value?.id
                      }
                      error={!!errors?.responsible}
                      helperText={errors?.responsible?.message}
                      disabled={isFormDisabled}
                    />
                  )}
                />
              </Grid>

              {ticket && (
                <Grid item md={6}>
                  <Controller
                    name="isActive"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={field.value || false}
                            {...field}
                            disabled={isFormDisabled}
                          />
                        }
                        label="Is Active?"
                        disabled={isFormDisabled}
                      />
                    )}
                  />
                </Grid>
              )}
            </>
          )}

          <Grid item md={12}>
            <FormHelperText>Description*</FormHelperText>
            <Controller
              name="description"
              control={control}
              render={({ field: { onChange } }) => (
                <RichTextEditor
                  initialValue={ticket?.description || "<p>Enter Ticket Description.</p>"}
                  onChange={onChange}
                  height={200}
                  disabled={isFormDisabled}
                />
              )}
            />
            {errors.description && (
              <FormHelperText error>{errors.description.message}</FormHelperText>
            )}
          </Grid>

          <Grid item md={12}>
            <FormHelperText>Attachments</FormHelperText>
            <Controller
              name="attachments"
              control={control}
              render={({ field }) => (
                <FileDropzone
                  multiple
                  maxFiles={5}
                  files={field.value || []}
                  onFileChange={(files) => field.onChange(files)}
                  disabled={isFormDisabled}
                />
              )}
            />
          </Grid>
        </Grid>
      </AppForm.Content>

      <AppForm.Actions>
        <span>
          <Button disabled={isBusy} onClick={() => navigate(-1)}>
            Cancel
          </Button>
        </span>
        <span>
          <Button
            variant="contained"
            disabled={isBusy || isFormDisabled}
            onClick={handleSubmit(handleSubmitTicket)}
          >
            {ticket ? "Update" : "Add"}
          </Button>
        </span>
      </AppForm.Actions>
    </AppForm>
  );
};

export default TicketForm;